import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { normalizeEmail, normalizePhone } from '../../../lib/utils/csvParser';

const MIN_PHONE_DIGITS = 10;
const MAX_PHONE_DIGITS = 15;
const MIN_EMAIL_LENGTH = 6;
const MAX_EMAIL_LENGTH = 254;

const formatInitialPhone = (value) => {
  if (!value?.trim()) return '+';
  const trimmed = value.trim().replace(/[\s\-().]/g, '');
  if (trimmed.startsWith('+')) return trimmed;
  if (trimmed.startsWith('00')) return `+${trimmed.slice(2)}`;
  return `+${trimmed.replace(/\D/g, '')}`;
};

const formatPhoneInput = (value) => {
  if (!value) return '+';
  let next = value.startsWith('+') ? value : `+${value.replace(/\+/g, '')}`;
  return `+${next.slice(1).replace(/\D/g, '')}`;
};

const EditContactModal = ({ isOpen, onClose, candidate, onSave }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setEmail(candidate?.email || '');
      setPhone(formatInitialPhone(candidate?.phone));
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen, candidate?.email, candidate?.phone]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !saving) onClose();
  };

  const validateForm = () => {
    const errors = {};
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (trimmedEmail) {
      if (trimmedEmail.length < MIN_EMAIL_LENGTH || trimmedEmail.length > MAX_EMAIL_LENGTH) {
        errors.email = t('candidateProfile.emailLength', { min: MIN_EMAIL_LENGTH, max: MAX_EMAIL_LENGTH });
      } else if (!normalizeEmail(trimmedEmail)) {
        errors.email = t('candidateProfile.emailInvalid');
      }
    }

    if (trimmedPhone && trimmedPhone !== '+') {
      const digitCount = trimmedPhone.replace(/\D/g, '').length;
      if (digitCount < MIN_PHONE_DIGITS || digitCount > MAX_PHONE_DIGITS) {
        errors.phone = t('candidateProfile.phoneLength', { min: MIN_PHONE_DIGITS, max: MAX_PHONE_DIGITS });
      } else if (!normalizePhone(trimmedPhone)) {
        errors.phone = t('candidateProfile.phoneInvalid');
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneInput(e.target.value));
    if (fieldErrors.phone) {
      setFieldErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (fieldErrors.email) {
      setFieldErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const normalizedPhone = trimmedPhone && trimmedPhone !== '+'
      ? normalizePhone(trimmedPhone)
      : '';

    try {
      await onSave({
        email: trimmedEmail ? (normalizeEmail(trimmedEmail) || trimmedEmail) : '',
        phone: normalizedPhone,
      });
      onClose();
    } catch (err) {
      setError(err.message || t('candidateProfile.updateContactFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-contact-dialog-title"
    >
      <div
        className="bg-card rounded-lg border border-border shadow-elevation-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h2 id="edit-contact-dialog-title" className="text-lg font-semibold text-foreground">
              {t('candidateProfile.editContactTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('candidateProfile.editContactSubtitle', { name: candidate?.name })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <Input
            type="text"
            inputMode="email"
            autoComplete="email"
            label={t('candidateProfile.email')}
            value={email}
            onChange={handleEmailChange}
            placeholder={t('candidateProfile.emailPlaceholder')}
            disabled={saving}
            error={fieldErrors.email}
            description={t('candidateProfile.emailDescription')}
            maxLength={MAX_EMAIL_LENGTH}
          />

          <Input
            type="text"
            inputMode="tel"
            autoComplete="tel"
            label={t('candidateProfile.phone')}
            value={phone}
            onChange={handlePhoneChange}
            disabled={saving}
            className="font-data"
            error={fieldErrors.phone}
            description={t('candidateProfile.phoneDescription')}
            maxLength={MAX_PHONE_DIGITS + 1}
          />

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContactModal;
